import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Field } from '../../components/ui/Field'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Pagination } from '../../components/ui/Pagination'
import { useClientPagination } from '../../hooks/useClientPagination'

type Status = 'active' | 'expired' | 'terminated' | 'pending_renewal'
type Type = 'furnizare' | 'servicii' | 'distributie' | 'consultanta' | 'inchiriere' | 'altele'

interface User {
  id: number
  name: string
  email: string
}

interface Contract {
  id: number
  type: Type
  supplier: string
  value: string | null
  startDate: string
  endDate: string
  responsibleId: number | null
  responsible: User | null
  docLink: string | null
  autoRenew: boolean
  status: Status
}

const TYPE_LABELS: Record<Type, string> = {
  furnizare: 'Furnizare',
  servicii: 'Servicii',
  distributie: 'Distribuție',
  consultanta: 'Consultanță',
  inchiriere: 'Închiriere',
  altele: 'Altele',
}

const STATUS_LABELS: Record<Status, string> = {
  active: 'Activ',
  expired: 'Expirat',
  terminated: 'Reziliat',
  pending_renewal: 'În reînnoire',
}
const STATUS_COLORS: Record<Status, string> = {
  active: 'bg-b4l-green-light text-b4l-green-dark',
  expired: 'bg-b4l-red-light text-b4l-red-dark',
  terminated: 'bg-b4l-bg text-b4l-text-muted',
  pending_renewal: 'bg-b4l-amber-light text-b4l-amber-dark',
}

export function ContractePage() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('contracts:write')

  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Contract | null>(null)

  const query = useQuery<{ contracts: Contract[] }>({
    queryKey: ['contracts'],
    queryFn: async () => (await api.get<{ contracts: Contract[] }>('/contracts')).data,
  })

  const usersQuery = useQuery<{ users: User[] }>({
    queryKey: ['users'],
    queryFn: async () => (await api.get<{ users: User[] }>('/settings/users/echipa')).data,
    enabled: canWrite,
  })

  const createMutation = useMutation({
    mutationFn: async (data: Partial<Contract>) => (await api.post('/contracts', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Contract adăugat')
      setCreateOpen(false)
    },
    onError: showError,
  })

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<Contract> }) =>
      (await api.patch(`/contracts/${id}`, data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Salvat')
      setEditTarget(null)
    },
    onError: showError,
  })

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: number; status: Status }) =>
      (await api.patch(`/contracts/${id}`, { status })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contracts'] }),
    onError: showError,
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/contracts/${id}`)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] })
      toast.success('Șters')
    },
  })

  const contracts = useMemo(() => query.data?.contracts ?? [], [query.data])
  const pag = useClientPagination(contracts, 10)
  const users = useMemo(() => usersQuery.data?.users ?? [], [usersQuery.data])

  const today = useMemo(() => new Date(), [])
  const isExpiringSoon = (endIso: string) => {
    const end = new Date(endIso)
    const days = Math.floor((end.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return days >= 0 && days <= 30
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Contracte</h1>
        {canWrite && <Button onClick={() => setCreateOpen(true)}>+ Contract nou</Button>}
      </div>

      <Card description="Registrul contractelor cu furnizorii și partenerii. Sortat după data expirării. Marcate vizual contractele care expiră în următoarele 30 zile.">
        {query.isLoading ? (
          <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
        ) : contracts.length === 0 ? (
          <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
            Niciun contract. {canWrite && 'Apasă „+ Contract nou”.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                  <th className="py-2 pr-3 font-medium">Furnizor</th>
                  <th className="py-2 pr-3 font-medium">Tip</th>
                  <th className="py-2 pr-3 font-medium text-right">Valoare</th>
                  <th className="py-2 pr-3 font-medium">Început</th>
                  <th className="py-2 pr-3 font-medium">Sfârșit</th>
                  <th className="py-2 pr-3 font-medium">Responsabil</th>
                  <th className="py-2 pr-3 font-medium">Reînnoire automată</th>
                  <th className="py-2 pr-3 font-medium">Stare</th>
                  <th className="py-2 pr-3 font-medium">Doc</th>
                  {canWrite && <th className="py-2 pr-3 font-medium">Acțiuni</th>}
                </tr>
              </thead>
              <tbody>
                {pag.paginated.map((c) => (
                  <tr key={c.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 max-w-[200px] truncate" title={c.supplier}>
                      {c.supplier}
                    </td>
                    <td className="py-2 pr-3 text-xs">{TYPE_LABELS[c.type]}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-right">
                      {c.value ? Number(c.value).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs whitespace-nowrap">
                      {new Date(c.startDate).toLocaleDateString('en-GB')}
                    </td>
                    <td
                      className={`py-2 pr-3 text-xs whitespace-nowrap ${
                        c.status === 'active' && isExpiringSoon(c.endDate)
                          ? 'text-b4l-amber-dark font-medium'
                          : ''
                      }`}
                    >
                      {new Date(c.endDate).toLocaleDateString('en-GB')}
                      {c.status === 'active' && isExpiringSoon(c.endDate) && ' ⚠'}
                    </td>
                    <td className="py-2 pr-3 text-xs">{c.responsible?.name ?? '—'}</td>
                    <td className="py-2 pr-3 text-xs">{c.autoRenew ? 'da' : 'nu'}</td>
                    <td className="py-2 pr-3">
                      {canWrite ? (
                        <select
                          value={c.status}
                          onChange={(e) =>
                            statusMutation.mutate({ id: c.id, status: e.target.value as Status })
                          }
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span
                          className={`rounded px-2 py-0.5 text-[11px] ${STATUS_COLORS[c.status]}`}
                        >
                          {STATUS_LABELS[c.status]}
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {c.docLink ? (
                        <a
                          href={c.docLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-b4l-blue-dark underline"
                        >
                          deschide ↗
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    {canWrite && (
                      <td className="py-2 pr-3">
                        <div className="flex gap-1">
                          <Button
                            variant="secondary"
                            onClick={() => setEditTarget(c)}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Editează
                          </Button>
                          <Button
                            variant="danger"
                            onClick={() => {
                              if (confirm(`Ștergi contractul cu ${c.supplier}?`))
                                deleteMutation.mutate(c.id)
                            }}
                            className="!px-2 !py-1 !text-xs"
                          >
                            Șterge
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3">
          <Pagination
            page={pag.page}
            pageSize={pag.pageSize}
            total={pag.total}
            onPageChange={pag.setPage}
            onPageSizeChange={pag.setPageSize}
            itemLabel="contracte"
          />
        </div>
      </Card>

      {createOpen && (
        <ContractFormModal
          title="Contract nou"
          submitLabel="Adaugă"
          users={users}
          onClose={() => setCreateOpen(false)}
          onSubmit={(d) => createMutation.mutate(d)}
          submitting={createMutation.isPending}
        />
      )}
      {editTarget && (
        <ContractFormModal
          title={`Editează contractul cu ${editTarget.supplier}`}
          submitLabel="Salvează"
          initial={editTarget}
          users={users}
          onClose={() => setEditTarget(null)}
          onSubmit={(d) => updateMutation.mutate({ id: editTarget.id, data: d })}
          submitting={updateMutation.isPending}
        />
      )}
    </div>
  )
}

function ContractFormModal({
  title,
  submitLabel,
  initial,
  users,
  onClose,
  onSubmit,
  submitting,
}: {
  title: string
  submitLabel: string
  initial?: Contract
  users: User[]
  onClose: () => void
  onSubmit: (d: Partial<Contract>) => void
  submitting: boolean
}) {
  const [type, setType] = useState<Type>(initial?.type ?? 'furnizare')
  const [supplier, setSupplier] = useState(initial?.supplier ?? '')
  const [value, setValue] = useState(initial?.value ?? '')
  const [startDate, setStartDate] = useState(
    initial?.startDate ? initial.startDate.slice(0, 10) : '',
  )
  const [endDate, setEndDate] = useState(initial?.endDate ? initial.endDate.slice(0, 10) : '')
  const [responsibleId, setResponsibleId] = useState<string>(
    initial?.responsibleId ? String(initial.responsibleId) : '',
  )
  const [docLink, setDocLink] = useState(initial?.docLink ?? '')
  const [autoRenew, setAutoRenew] = useState(initial?.autoRenew ?? false)

  const submit = () => {
    if (!supplier.trim() || !startDate || !endDate) {
      toast.error('Furnizor, început și sfârșit sunt obligatorii')
      return
    }
    onSubmit({
      type,
      supplier: supplier.trim(),
      value: value ? String(Number(value)) : null,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      responsibleId: responsibleId ? Number(responsibleId) : null,
      docLink: docLink.trim() || null,
      autoRenew,
    } as unknown as Partial<Contract>)
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Anulează
          </Button>
          <Button onClick={submit} loading={submitting}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Furnizor" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Tip</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as Type)}
              className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
            >
              {(Object.keys(TYPE_LABELS) as Type[]).map((x) => (
                <option key={x} value={x}>
                  {TYPE_LABELS[x]}
                </option>
              ))}
            </select>
          </div>
          <Field
            label="Valoare RON"
            type="number"
            step="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Început"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <Field
            label="Sfârșit"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Responsabil</label>
          <select
            value={responsibleId}
            onChange={(e) => setResponsibleId(e.target.value)}
            className="w-full rounded border border-b4l-border px-3 py-2 text-sm"
          >
            <option value="">— niciunul —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} ({u.email})
              </option>
            ))}
          </select>
        </div>
        <Field label="Link document" value={docLink} onChange={(e) => setDocLink(e.target.value)} />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoRenew}
            onChange={(e) => setAutoRenew(e.target.checked)}
            className="h-4 w-4"
          />
          Reînnoire automată
        </label>
      </div>
    </Modal>
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
